import { TFunc } from '@Translations';
import {
	ConfigurationDataSourceType,
	SensorConfigFormShape,
} from '@Types/configurations.types';
import * as yup from 'yup';
const SensorSchema = (t: TFunc, formData: SensorConfigFormShape) => {
	const schema = {
		sensorType: yup
			.string()
			.required(t('Sensor Type is Required'))
			.typeError(t('Sensor Type must be a string')),
		sensorId: yup
			.string()
			.required(t('Sensor ID is required'))
			.typeError(t('Sensor ID must be a string'))
			.test(
				'only supported characters',
				t(
					'Supported characters: alphanumerics, dash, underscore, period, colon, space.',
				),
				value => !/[^a-zA-Z0-9_.\- :]/.test(value || ''),
			)
			.matches(
				/[a-zA-Z0-9]/,
				t(
					'Sensor ID is required.  Must contain at least one alphanumeric character.',
				),
			),
		sensorAlias: yup
			.string()
			.required(t('Sensor Alias is required.'))
			.typeError(t('Sensor Alias must be a string.')),
		dataSourceId: yup
			.string()
			.notRequired()
			.typeError(t('Sensor Data Source must be a string.')),
		dataSourceType: yup
			.string()
			.notRequired()
			.typeError(t('Sensor Data Source Type must be a string.')),
		location: yup
			.string()
			.notRequired()
			.matches(
				/(^ *-?(90.0|(([1-8]\d|\d)\.\d{1,14})) *, *-?(180.0|(1[0-7]\d|[1-9]\d|\d)\.\d{1,14}) *){1}$|^$/,
				t(
					'Coordinates must be valid lat/lng with at least 1 decimal place.',
				),
			)
			.typeError(t('Sensor Location must be a string.')),
		sensorIdField: yup
			.string()
			.notRequired()
			.typeError(t('Sensor ID field must be a string.')),
		sensorTable: yup
			.string()
			.notRequired()
			.typeError(t('Sensor type must be a string.')),
		sensorDateField: yup
			.string()
			.notRequired()
			.typeError(t('Sensor date field must be a string.')),
		sensorTimeField: yup
			.string()
			.notRequired()
			.typeError(t('Sensor time field must be a string.')),
		sensorChannelField: yup
			.string()
			.notRequired()
			.typeError(t('Sensor channel field must be a string.')),
		unit: yup
			.string()
			.when('dataSourceType', {
				is: val => val !== ConfigurationDataSourceType.Form,
				then: (schema: yup.ObjectSchema) => schema.notRequired(),
				otherwise: (schema: yup.ObjectSchema) =>
					schema.required(t('Source Unit is Required')),
			})
			.typeError(t('Sensor source unit must be a string.')),
		sensorUnitConversion: yup
			.string()
			.notRequired()
			.typeError(t('Sensor unit conversion must be a string.')),
		assetId: yup
			.string()
			.notRequired()
			.typeError(t('Asset ID must be a string.')),
		interpolationEnabled: yup
			.string()
			.notRequired()
			.typeError(t('Interpolation Enabled is either True or False.')),
		interpolationMethod: yup
			.string()
			.notRequired()
			.oneOf(['lkv', ''], t('Interpolation Method can only be lkv.')),
		tagValue: yup
			.string()
			.notRequired()
			.when('dataSourceType', {
				is: 'OPC-UA',
				then: yup
					.string()
					.required(t('Node ID is required.'))
					.matches(
						/^.*ns=.*$/,
						t(
							'Node ID must be in OPC-UA Node ID format (ns=xxxx).',
						),
					),
			})
			.typeError(t('Tag Value must be a string.')),
		isPhysical: yup.string().required(),
	};

	const {
		sensorType,
		tankType,
		isPhysical,
		collectionInterval,
		tankDiameter,
		tankOffset,
	} = formData;

	if (isPhysical !== 'false' && collectionInterval) {
		(schema as Record<string, unknown>).collectionInterval = yup
			.number()
			.notRequired()
			.typeError(t('Sensor Interval must be a number.'))
			.integer(t('Sensor Interval must be an integer.'))
			.positive(t('Sensor Interval must be a positive number.'))
			.min(60, t('Sensor Interval must be integer >= 60.'));
	}

	if (sensorType === 'Tank Level') {
		if (tankType === 'cylindrical') {
			if (tankDiameter)
				(schema as Record<string, unknown>).tankDiameter = yup
					.number()
					.notRequired()
					.typeError(t('Diameter must be a number.'))
					.min(0, t('Diameter must be greater or equal to 0.'));
			if (tankOffset)
				(schema as Record<string, unknown>).tankOffset = yup
					.number()
					.notRequired()
					.typeError(t('Offset must be a number.'))
					.min(0, t('Offset must be greater or equal to 0.'));
		} else {
			(schema as Record<string, unknown>).tankCurveId = yup
				.string()
				.notRequired()
				.typeError(t('Tank curve ID must be a string.'));
		}
	}
	return yup.object(schema);
};

export default SensorSchema;
